import React, { useState } from 'react';
import moment from 'moment';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  FormControlLabel,
  TextField,
  Typography,
  Box,
  Divider,
  ButtonGroup,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core';
import { InfoOutlined, History } from '@material-ui/icons';
import { useTheme } from '@material-ui/core/styles';
import { useToggle } from 'src/hooks';
import SingleDatePicker from 'src/components/SingleDatePicker';
import { Timezone } from 'src/constants';

// Helpers
export const getIxUserByUsername = (username, users) => {
  if (!username) return 0;
  return users?.find(user => user.username === username)?.ixUser || 0;
};

export const getUsernameByID = (ixUser, users) => {
  if (!ixUser) return '';
  return users?.find(user => user.ixUser === ixUser)?.UserID || '';
};

export const formatDate = dateString => {
  if (!dateString) return '-';
  const date = moment(dateString);
  return date.isValid() ? date.format('MM/DD/YYYY') : dateString;
};

// Recon dialog
const ReconDialog = ({
  open,
  onClose,
  onUpdate,
  reconDetails,
  onReconDetailsChange,
  getUsernameByID,
  reconUpdateOpen,
  openReconUpdate,
  closeReconUpdate
  }) => {
  const theme = useTheme();

  return (
    <Dialog open={open} maxWidth="xs" fullWidth onClose={onClose}>
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" color="textSecondary">
            {reconUpdateOpen ? 'Update Recon Details' : 'Recon Details'}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            gridColumnGap={theme.spacing(1)}
          >
            {!reconUpdateOpen && (
              <>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={openReconUpdate}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={onClose}
                >
                  Close
                </Button>
              </>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent style={{ maxHeight: 600, padding: theme.spacing(3) }}>
        <Box
          display="flex"
          flexDirection="column"
          gridRowGap={theme.spacing(2)}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(reconDetails?.recon_status)}
                onChange={() => {
                  if (!reconUpdateOpen) return;
                  const newStatus = !reconDetails.recon_status;
                  onReconDetailsChange('recon_status', newStatus);
                  if (!newStatus) onReconDetailsChange('recon_remarks', '');
                  onReconDetailsChange(
                    'recon_reflectDate',
                    newStatus ? reconDetails.jDate : ''
                  );
                }}
                color="primary"
              />
            }
            label="Reconciled"
          />

          {Boolean(reconDetails?.recon_status) && (
            <>
              {!reconUpdateOpen && (
                <>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="User"
                    value={getUsernameByID(reconDetails.recon_ixUser)}
                    InputProps={{ readOnly: true }}
                  />
                  <SingleDatePicker
                    fullWidth
                    variant="outlined"
                    value={
                      reconDetails.recon_date
                        ? moment(reconDetails.recon_date).format('YYYY-MM-DD')
                        : ''
                    }
                    label="Recon Date"
                    InputProps={{ readOnly: true }}
                  />
                </>
              )}

              <SingleDatePicker
                fullWidth
                variant="outlined"
                value={reconDetails.recon_reflectDate}
                label="Reflect Date"
                onChange={e => {
                  const val = e.target.value;
                  onReconDetailsChange(
                    'recon_reflectDate',
                    val ? moment(val).format('YYYY-MM-DD') + 'T00:00:00' : ''
                  );
                }}
                InputProps={{ readOnly: !reconUpdateOpen }}
              />

              {moment(reconDetails.recon_reflectDate).diff(
                moment(reconDetails.jDate),
                'days'
              ) > 15 && (
                <SingleDatePicker
                  fullWidth
                  variant="outlined"
                  value={reconDetails.confirm_date}
                  label="Confirm Reflect Date"
                  disabled={!reconUpdateOpen}
                  onChange={e =>
                    onReconDetailsChange(
                      'confirm_date',
                      moment(e.target.value).format('YYYY-MM-DD') + 'T00:00:00'
                    )
                  }
                  InputProps={{ readOnly: !reconUpdateOpen }}
                />
              )}
            </>
          )}

          <TextField
            InputProps={{ readOnly: !reconUpdateOpen }}
            fullWidth
            multiline
            maxRows={4}
            minRows={4}
            variant="outlined"
            value={reconDetails.recon_remarks}
            label="Remarks"
            onChange={e =>
              onReconDetailsChange('recon_remarks', e.target.value)
            }
          />
        </Box>
      </DialogContent>

      {reconUpdateOpen && (
        <>
          <Divider />
          <DialogActions>
            <Button size="small" color="primary" onClick={closeReconUpdate}>
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={onUpdate}
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

// Hook holding recon dialog state
export const useRecon = () => {
  const [reconDetails, setReconDetails] = useState({
    id: '',
    acc_id: '',
    recon_status: 0,
    recon_reflectDate: '',
    confirm_date: '',
    recon_remarks: '',
    recon_date: '',
    recon_ixUser: 0,
    jDate: ''
  });

  const [reconUpdateOpen, openReconUpdate, closeReconUpdate] = useToggle();
  const [reconDetailsOpen, openReconDetails, closeReconDetails] = useToggle();

  const handleSetReconDetails = ({
    id,
    acc_id,
    recon_reflectDate,
    recon_remarks,
    recon_date,
    recon_ixUser,
    jDate,
    recon_status
  }) => {
    const reflectDate = recon_reflectDate
      ? moment(recon_reflectDate).format('YYYY-MM-DD')
      : '';

    setReconDetails({
      id,
      acc_id,
      recon_status,
      recon_reflectDate: reflectDate,
      recon_remarks,
      recon_date: recon_date || '',
      recon_ixUser,
      jDate: moment(jDate).format('YYYY-MM-DD'),
      confirm_date: reflectDate
    });
  };

  const onReconDetailsChange = (field, val) => {
    setReconDetails(state => ({ ...state, [field]: val }));
  };

  return {
    reconDetails,
    reconUpdateOpen,
    reconDetailsOpen,
    openReconUpdate,
    closeReconUpdate,
    openReconDetails,
    closeReconDetails,
    handleSetReconDetails,
    onReconDetailsChange
  };
};

// Save recon API wrapper
export const saveRecon = async ({
  id,
  recon_status,
  ixAcc,
  recon_reflectDate,
  remarks = '',
  confirm_date = '',
  recon_date = moment().format('YYYY-MM-DDTHH:mm:ss'),
  setJournalEntries,
  notify,
  current_user,
  request,
  Timezone,
  users,
  popup
}) => {
  const requestData = { id, status: recon_status, ixAcc, remarks };
  if (recon_status) requestData.reflectDate = recon_reflectDate + Timezone;
  if (confirm_date) requestData.confirm_date = confirm_date + Timezone;

  const res = await request.post('/trans/recon', requestData);
  if (!res.success) {
    popup(res.error.data, { autoClose: true });
    return false;
  }

  if (setJournalEntries) {
    setJournalEntries({
      recon_status,
      status: recon_status,
      recon_ixUser:
        users?.find(u => u.username === current_user?.username)?.ixUser || 0,
      recon_date,
      recon_reflectDate,
      recon_remarks: remarks
    });
  }

  notify.success(res.data || 'Recon updated.');
  return true;
};

// Hook exposing recon actions
export const useReconActions = ({
  prompt,
  current_user,
  setJournalEntries,
  notify,
  request,
  Timezone,
  users,
  popup
}) => {
  const {
    reconDetails,
    reconUpdateOpen,
    reconDetailsOpen,
    openReconUpdate,
    closeReconUpdate,
    openReconDetails,
    closeReconDetails,
    handleSetReconDetails,
    onReconDetailsChange
  } = useRecon();

  const [logsOpen, setLogsOpen] = useState(false);

  const viewLogs = details => {
    handleSetReconDetails(details);
    setLogsOpen(true);
  };

  const handleShowReconDetails = details => {
    handleSetReconDetails(details);
    openReconDetails();
  };


  const handleReconToggle = trans => {
    const {
      id,
      recon_status,
      acc_id,
      jDate,
      remarks = '',
      recon_ixUser,
      recon_date,
      CheckDateCleared,
      recon_reflectDate
    } = trans;

    if (recon_status) {
      return prompt({
        title: <Typography variant="h5">Confirm</Typography>,
        body: (
          <Typography variant="body2">
            {`This has already been reconned by ${getUsernameByID(
              recon_ixUser,
              users
            )} on ${moment(recon_date).format(
              'MM/DD/YYYY hh:mm A'
            )}. Are you sure you like to untick?`}
          </Typography>
        ),
        onOk: () => {
          handleSetReconDetails({
            ...trans,
            recon_status: 0
          });
          openReconDetails();
          openReconUpdate();
        },
        okText: 'Proceed',
        cancelProps: { color: 'primary' },
        okProps: { variant: 'contained', color: 'primary' }
      });
    }

    // tick - direct save
    saveRecon({
      id,
      recon_status: 1,
      ixAcc: acc_id,
      recon_reflectDate: CheckDateCleared
        ? moment(CheckDateCleared).format('YYYY-MM-DDTHH:mm:ss')
        : jDate,
      remarks,
      setJournalEntries,
      notify,
      current_user,
      request,
      Timezone,
      users,
      popup
    });
  };

  // Dialog save handler
  const handleReconUpdate = async () => {
    const {
      id,
      recon_status,
      acc_id: ixAcc,
      recon_reflectDate,
      jDate,
      recon_remarks: remarks,
      confirm_date,
      recon_date
    } = reconDetails;

    if (!recon_status) {
      if (!remarks) {
        notify('Remarks required.', { type: 'error' });
        return;
      }

      const success = await saveRecon({
        id,
        recon_status,
        ixAcc,
        recon_reflectDate,
        recon_date,
        remarks,
        setJournalEntries,
        notify,
        current_user,
        request,
        Timezone,
        users,
        popup
      });

      if (success) {
        closeReconUpdate();
        closeReconDetails();
      }
      return;
    }

    const dateDiff = moment(recon_reflectDate).diff(moment(jDate), 'days');

    if (dateDiff < -15) {
      notify(
        "Reflect date can't be more than 15 days earlier than transaction date.",
        {
          type: 'error'
        }
      );
      return;
    }
    if (dateDiff > 15 && recon_reflectDate !== confirm_date) {
      notify('Reflect date and confirmation date did not match.', {
        type: 'error'
      });
      return;
    }
    if (dateDiff > 15 && !remarks) {
      notify(
        `Reflect date is ${dateDiff} days later than transaction date. Remarks required.`,
        { type: 'error' }
      );
      return;
    }

    const reconData = {
      id,
      recon_status,
      ixAcc,
      recon_reflectDate,
      remarks,
      setJournalEntries,
      notify,
      current_user,
      request,
      Timezone
    };

    if (dateDiff > 15) reconData.confirm_date = confirm_date;

    const success = await saveRecon({ ...reconData, users, popup });
    if (success) {
      closeReconUpdate();
      closeReconDetails();
    }
  };

  return {
    reconDetails,
    reconUpdateOpen,
    reconDetailsOpen,
    logsOpen,
    openReconUpdate,
    closeReconUpdate,
    openReconDetails,
    closeReconDetails,
    handleSetReconDetails,
    onReconDetailsChange,
    viewLogs,
    handleShowReconDetails,
    handleReconToggle,
    handleReconDialogUpdate: handleReconUpdate,
    setLogsOpen
  };
};

// Recon UI table
export const ReconUI = ({
  trans,
  handleReconToggle,
  viewLogs,
  handleShowReconDetails
}) => {
  const isReconciled = Boolean(trans?.recon_status);
  const reflectDate = trans?.recon_reflectDate;

  return (
    <Table sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <TableHead>
        <TableRow>
          <TableCell>Recon Status</TableCell>
          <TableCell>Recon Reflect Date</TableCell>
          <TableCell>Recon Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>
            <Checkbox
              size="small"
              checked={isReconciled}
              onChange={() => handleReconToggle(trans)}
              color="primary"
            />
          </TableCell>
          <TableCell>
            {isReconciled && reflectDate
              ? moment(reflectDate).format('MM/DD/YYYY')
              : '-'}
          </TableCell>
          <TableCell>
            <ButtonGroup size="small" variant="contained" color="primary">
              <Tooltip title="View Recon Details">
                <Button onClick={() => handleShowReconDetails(trans)}>
                  <InfoOutlined fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title="View Logs">
                <Button onClick={() => viewLogs(trans)}>
                  <History fontSize="small" />
                </Button>
              </Tooltip>
            </ButtonGroup>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

export const createReconPromptHandler = (
  rest,
  ixAcc,
  handleSetReconDetails,
  openReconDetails,
  openReconUpdate
) => () => {
  handleSetReconDetails({
    id: rest?.id?.value || 0,
    acc_id: ixAcc.value,
    recon_status: 0,
    recon_reflectDate: '',
    recon_remarks: '',
    recon_ixUser: rest?.recon_ixUser || 0,
    jDate: rest?.journalDate?.value || new Date()
  });
  openReconDetails();
  openReconUpdate();
};

export default ReconDialog;
